import { isImageContentType, isImageFileUrl } from "@/helpers/is-image-file";

export type PrivateFileRecord = Record<string, unknown> | null | undefined;

export type PrivateFileTool = "survey" | "places" | "ideas";

export type PrivateFileUploadTool = "survey" | "map" | "brainstormer";

const NON_IMAGE_PATH_PATTERN =
  /\.(pdf|doc|docx|zip|xls|xlsx|ppt|pptx|rtf|mp3|mp4|wav)$/i;

export function uploadToolToPrivateFileTool(
  tool?: PrivateFileUploadTool
): PrivateFileTool {
  if (tool === "brainstormer") return "ideas";
  if (tool === "map") return "places";
  return "survey";
}

/** Read presigned-file-url from API / upload payloads (any key shape). */
export function getPresignedFileUrl(
  source: PrivateFileRecord
): string | undefined {
  if (!source) return undefined;

  const value =
    source.presignedFileUrl ??
    source.presigned_file_url ??
    source["presigned-file-url"];

  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Read app download url (file-url) — survey / places. */
export function getAppFileDownloadUrl(
  source: PrivateFileRecord
): string | undefined {
  if (!source) return undefined;

  const value =
    source.url ?? source.fileUrl ?? source.file_url ?? source["file-url"];

  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Read idea media-url from API payloads (any key shape). */
export function getIdeaMediaUrl(source: PrivateFileRecord): string | undefined {
  if (!source) return undefined;

  const value = source.mediaUrl ?? source["media-url"];

  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * App download URL for display/submit — never presigned.
 * - ideas: media-url, then file-url
 * - survey / places: file-url
 */
export function getPrivateFileDownloadUrl(
  source: PrivateFileRecord,
  tool: PrivateFileTool = "survey"
): string | undefined {
  if (!source) return undefined;

  if (tool === "ideas") {
    return getIdeaMediaUrl(source) ?? getAppFileDownloadUrl(source);
  }

  return (
    getAppFileDownloadUrl(source) ??
    (typeof source.fileUrl === "string" ? source.fileUrl : undefined)
  );
}

/** Presigned + download URLs from any private-file API/upload record. */
export function getPrivateFileUrlsFromRecord(
  source: PrivateFileRecord,
  tool: PrivateFileTool = "survey"
): { downloadUrl?: string; presignedFileUrl?: string } {
  return {
    downloadUrl: getPrivateFileDownloadUrl(source, tool),
    presignedFileUrl: getPresignedFileUrl(source)
  };
}

/** Places pin form state from a marker file answer. */
export function getPlaceImageFormDetails(source: PrivateFileRecord): {
  fileUrl?: string;
  presignedFileUrl?: string;
} {
  const { downloadUrl, presignedFileUrl } = getPrivateFileUrlsFromRecord(
    source,
    "places"
  );

  return { fileUrl: downloadUrl, presignedFileUrl };
}

export function normalizePrivateFileUploadResponse<
  T extends Record<string, unknown>
>(
  file: T,
  options?: { tool?: PrivateFileUploadTool }
): T & {
  url?: string;
  presignedFileUrl?: string;
  mediaUrl?: string;
} {
  if (!file) return file;

  const fileTool = uploadToolToPrivateFileTool(options?.tool);
  const submissionUrl =
    getPrivateFileDownloadUrl(file, fileTool) ??
    (file.url as string | undefined);

  return {
    ...file,
    url: submissionUrl,
    presignedFileUrl: getPresignedFileUrl(file),
    ...(fileTool === "ideas"
      ? { mediaUrl: getIdeaMediaUrl(file) ?? submissionUrl }
      : {})
  };
}

/** Whether presigned URL should drive private-bucket image display (not file-url). */
export function shouldUsePresignedForPrivateDisplay(
  presignedFileUrl: string | undefined,
  options?: {
    contentType?: string;
    fileName?: string;
    /** Pin / idea media is always image when presigned is present. */
    assumeImage?: boolean;
  }
): boolean {
  if (!presignedFileUrl) return false;

  if (options?.contentType || options?.fileName) {
    return isImageContentType(options.contentType, options.fileName);
  }

  if (options?.assumeImage) {
    return !NON_IMAGE_PATH_PATTERN.test(presignedFileUrl.split("?")[0]);
  }

  if (isImageFileUrl(presignedFileUrl)) return true;

  if (
    /amazonaws\.com/i.test(presignedFileUrl) &&
    !NON_IMAGE_PATH_PATTERN.test(presignedFileUrl.split("?")[0])
  ) {
    return true;
  }

  return false;
}
